/**
 * TOSS Web Application - Client portal header
 *
 * The portal's own filling of the shared `TopBar` (`components/topbar/**`) — the same frame the
 * internal application draws, with the customer's controls in it. What differs between the two
 * bars is the contents of two slots and nothing else: since 2026-08-26 the heights, the icon
 * sizes, the badge and the z-index are the layer's, not this file's.
 *
 * **Left: the company, and only the company** (owner, 26.08.2026). The section name used to be
 * printed here on a computer, with the company as a second line under it. It is gone — not moved.
 * Which SCREEN you are on is answered by the lit row of the sidebar, and on a phone by the bottom
 * bar; which CABINET you are in is answered nowhere else, changes the meaning of every number on
 * the screen, and for an account with two legal entities is also the control that switches it.
 * The internal application arrived at the same shape from the other side: its pages name
 * themselves through `PageHeader`, and its bar had been printing an empty title for a year.
 *
 * Both widths therefore render the SAME element in that slot — `ClientCompanySwitcher`, at the
 * placement its breakpoint owns (`appbar` ≤767, `header` ≥768). Both render for a single-company
 * account too: they are headings that happen to be controls.
 *
 * **Right:** the three time zones, a rule, and the bell. The bell is here at EVERY width since
 * release Р2 — «Уведомления» left the bottom bar when a sixth item did not fit into five
 * (`showInBottomNav: false` in `client-nav/clientNavigationConfig.ts`), so on a phone this badge
 * is the only unread count in the whole chrome. The two changes are one change.
 *
 * The bar is `fixed` on a phone, and that is not a preference: the app's global stylesheet puts
 * `overflow-x: hidden` on `<body>`, which makes the body itself a scroll container — and a
 * `sticky` element inside a scroll container that does not itself scroll never sticks to
 * anything. `ClientLayout` reserves the 64 px with a spacer. On tablet and desktop the shell
 * scrolls `<main>` and the header never moves anyway.
 *
 * ---------------------------------------------------------------------------
 * WHY THE BAR EXISTS ABOVE 767 AT ALL
 * ---------------------------------------------------------------------------
 *
 * Decision Р33 (2026-08-21) removed it because it printed the section name and the page printed
 * the same name again — «Заявки» over «Заявки» on seven screens out of nine. The owner brought it
 * back on 2026-08-24 for the wireframes, with the duplicate prevented at the page end
 * (`ClientPageHeader` became a toolbar and lost its `title`). The 26.08 change finishes that
 * thought: the bar no longer has a title to duplicate with. A screen that starts printing its own
 * heading again is free to — it is now the only one printing anything.
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import { TimeZoneClock, TopBar, TopBarDivider } from '@/components/topbar';
import { ConnectionStatusThread } from '@/components/ui/ConnectionThread';
import { ClientCompanySwitcher } from './client-nav/ClientCompanySwitcher';
import { ClientNotificationsBell } from './client-nav/ClientNotificationsBell';

// ============================================================================
// Types
// ============================================================================

export interface ClientHeaderProps extends HTMLAttributes<HTMLElement> {
  /** Additional class names */
  className?: string;
  /** Полоса или карточка — см. `TopBar`. На телефоне бар `fixed` и всегда полоса. */
  variant?: 'bar' | 'card';
}

// ============================================================================
// Component
// ============================================================================

export const ClientHeader = forwardRef<HTMLElement, ClientHeaderProps>(
  ({ className = '', variant, ...props }, ref) => {
    const { showMobileNav } = useResponsive();

    return (
      <TopBar
        ref={ref}
        className={className}
        variant={variant}
        // 64 on a phone as well, unlike the internal bar's 56: `ClientLayout` reserves exactly
        // this many pixels below the `fixed` bar, and the section bars of «Заявки» and
        // «Документы» measure themselves against it.
        phoneHeight={64}
        fixed={showMobileNav}
        leading={
          <div className="min-w-0 flex-1">
            <ClientCompanySwitcher placement={showMobileNav ? 'appbar' : 'header'} />
          </div>
        }
        actions={
          <>
            {/* Hidden below 768 by its own class — the phone's bar has room for the company and
                the bell, and nothing else. */}
            <TimeZoneClock />
            <TopBarDivider />
            <ClientNotificationsBell />
          </>
        }
        underline={<ConnectionStatusThread />}
        {...props}
      />
    );
  }
);

ClientHeader.displayName = 'ClientHeader';

export default ClientHeader;
